import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatCompensation } from '../utils/compensation';

interface Props {
  channelUrl: string;
  recipientProfileUuid: string;
  onClose: () => void;
  authenticityToken?: string | null;
}

/**
 * Share-a-job sheet: slides up inside the chat window (anchored to the
 * panel, not the page) so sharing a job never leaves the conversation.
 * Sending creates the invite server-side, which posts the job card into
 * the channel.
 */
const JobSelectorModal: React.FC<Props> = ({ channelUrl, recipientProfileUuid, onClose, authenticityToken }) => {
  const [query, setQuery] = useState('');
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [sharedIds, setSharedIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const resp = await axios.get('/api/v1/employer/jobs', { params: { published: true } });
        setJobs(resp.data.jobs || resp.data || []);
      } catch (e) {
        setError('Could not load your jobs. Try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const jobLocation = (j: any) =>
    [j.practice_city, j.practice_state, j.city, j.state, j.practice && j.practice.city, j.practice && j.practice.state]
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .join(', ');

  // The jobs API has no text search; the pool is small, so filter locally.
  const visibleJobs = jobs.filter((j: any) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [j.title, j.job_title, j.practice_name, j.company, j.practice && j.practice.name, jobLocation(j)]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });

  const handleShare = async (jobId?: number) => {
    if (!jobId || sendingId) return;
    setSendingId(jobId);
    setError(null);

    try {
      const csrf = authenticityToken || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch('/api/v1/employer/application_invites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        credentials: 'same-origin',
        body: JSON.stringify({ job_id: jobId, profile_id: recipientProfileUuid, channel_url: channelUrl })
      });

      if (response.status === 201) {
        setSharedIds((prev) => [...prev, jobId]);
        // Leave the sheet open briefly so the recruiter sees the Shared state
        setTimeout(onClose, 600);
        return;
      }
      if (response.status === 401) {
        window.location.href = '/users/sign_in';
        return;
      }
      const body = await response.json().catch(() => null);
      setError(body?.errors?.join(', ') || body?.error || 'Could not share the job. Try again.');
    } catch (e: any) {
      setError(e.message || 'Could not share the job. Try again.');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="dd-chat__sheet-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dd-chat__sheet" role="dialog" aria-label="Share a job">
        <div className="dd-chat__sheet-head">
          <div className="dd-chat__sheet-title">
            <i className="fas fa-briefcase" /> Share a job
          </div>
          <button type="button" className="dd-chat__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="dd-chat__sheet-search">
          <input
            className="dd-chat__sheet-input"
            placeholder="Search your jobs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="dd-chat__sheet-list">
          {loading ? (
            <div className="dd-chat__sheet-empty">Loading your jobs…</div>
          ) : visibleJobs.length === 0 ? (
            <div className="dd-chat__sheet-empty">No published jobs match.</div>
          ) : (
            visibleJobs.map((j: any) => {
              const pay = formatCompensation(j) || j.salary || j.compensation || null;
              const shared = sharedIds.includes(j.id);
              return (
                <div key={j.id} className="dd-chat__sheet-row">
                  <div className="dd-chat__sheet-row-main">
                    <div className="dd-chat__sheet-row-title">{j.title || j.job_title}</div>
                    <div className="dd-chat__sheet-row-meta">
                      {(j.practice_name || j.company || (j.practice && j.practice.name)) && (
                        <span>{j.practice_name || j.company || (j.practice && j.practice.name)}</span>
                      )}
                      {jobLocation(j) && <span>{jobLocation(j)}</span>}
                      {pay && <span className="dd-chat__sheet-pay">{pay}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`dd-chat__sheet-share ${shared ? 'dd-chat__sheet-share--done' : ''}`}
                    onClick={() => handleShare(j.id)}
                    disabled={!!sendingId || shared}
                  >
                    {shared ? 'Shared ✓' : sendingId === j.id ? 'Sharing…' : 'Share'}
                  </button>
                </div>
              );
            })
          )}
        </div>
        {error && <div className="dd-chat__sheet-error">{error}</div>}
      </div>
    </div>
  );
};

export default JobSelectorModal;
